import {
    WS_BRANDS,
    WS_CUSTOM_BRAND,
    WS_KIT_STEMS,
    WS_NEUTRAL_BRAND,
    WS_THEMES,
} from '../styles/wsTokens';
import {
    AA_CONTRAST,
    contrastRatio,
    mixSrgb,
    parseColor,
    readableInk,
    readableOnAccent,
    toHex,
} from './wsContrast';

/**
 * The brand kit, as the workspace reads it.
 *
 * The one source is `WorkTrial.brand_kit_snapshot` — a frozen copy taken at trial
 * creation. The workspace never re-derives a colour and never reads the kit
 * library, so re-skinning a kit cannot retro-change a running trial and a frozen
 * certificate config stays frozen. Everything here is pure: the React layer that
 * mounts it is `components/workspace/BrandProvider.js`.
 *
 * Two shapes of kit, one branch, made once:
 *
 *   - a snapshot whose kit id is one of the six shipped house kits sets
 *     `data-brand` to it, and `src/styles/brand-tokens.css` supplies its twelve
 *     input values;
 *   - any other snapshot sets `data-brand="custom"` and injects the same twelve
 *     values inline, resolved from its own `tokens` map.
 *
 * There is no second override layer and no house/employer boolean: the six pairs
 * are the whole kit, "house" is one of three kinds, and a kit that differs simply
 * carries different pairs (arbiter ruling 60).
 */

/** The six token keys a kit carries. Each maps to a `{light, dark}` pair. */
export const KIT_TOKEN_KEYS = Object.keys(WS_KIT_STEMS);

/** `BrandKit.kind` — a house kit is one of three kinds, not the negation of employer. */
export const KIT_KINDS = ['employer', 'house', 'certificate'];

/** Where the viewer's own theme choice lives. No backend: this is a viewer preference. */
export const THEME_STORAGE_KEY = 'ws-theme';

export const isHouseKit = (kitId) => WS_BRANDS.includes(kitId);

/**
 * Colour values arrive from a server document and land in a `style` attribute, so
 * they are matched against a colour grammar rather than trusted. Anything else —
 * a second declaration smuggled past a `;`, a `url(`, an empty string — fails and
 * takes the whole kit down the neutral-house path. A value the browser would
 * discard is as bad as an injected one: the ramp mixes from these, so one
 * unparseable input empties every token derived from it.
 */
const COLOR_PATTERN = /^(#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})|(rgb|hsl)a?\([^;{}()]*\)|(hwb|lab|lch|oklab|oklch)\([^;{}()]*\))$/i;

/* The keywords a kit is plausibly hand-authored with; everything else is a literal. */
const COLOR_KEYWORDS = [
    'transparent', 'currentcolor', 'white', 'black', 'silver', 'gray', 'maroon',
    'red', 'purple', 'fuchsia', 'green', 'lime', 'olive', 'yellow', 'navy',
    'blue', 'teal', 'aqua',
];

const isColorValue = (value) => {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    return COLOR_PATTERN.test(trimmed) || COLOR_KEYWORDS.includes(trimmed.toLowerCase());
};

/**
 * The six keys, each a `{light, dark}` pair of colours — or `null` if the map is
 * not exactly that. Five keys and seven keys are both wrong, and so is a bare
 * colour string where a pair belongs.
 */
export const normalizeTokens = (tokens) => {
    if (!tokens || typeof tokens !== 'object' || Array.isArray(tokens)) return null;
    if (Object.keys(tokens).length !== KIT_TOKEN_KEYS.length) return null;

    const normalized = {};
    for (const key of KIT_TOKEN_KEYS) {
        const pair = tokens[key];
        if (!pair || typeof pair !== 'object') return null;
        if (WS_THEMES.some((theme) => !isColorValue(pair[theme]))) return null;
        normalized[key] = { light: pair.light.trim(), dark: pair.dark.trim() };
    }
    return normalized;
};

/**
 * The surfaces accent-coloured **text** stands on, per theme, as
 * `src/styles/brand-tokens.css` mixes them.
 *
 * Mirrored here because CSS has no way to branch on contrast, and a word has to be
 * checked against the ground it will actually land on rather than against an
 * average. Three grounds and no more: the pane (cards, the rail), the canvas (the
 * page behind them) and the accent's own wash (chips, the active rail row, every
 * accent-tinted block) — the third is the strict one, because it is the accent
 * mixed into a neutral and therefore the closest surface to the accent itself.
 *
 * The percentages and the neutral bases are the sheet's own. If the ramp's mix
 * changes there, it changes here: they are one formula written twice, which is the
 * price of deriving in JS what CSS cannot.
 */
const INK_GROUNDS = Object.freeze({
    light: [
        { from: 'accent', percent: 12, base: '#F3F4F6' },   /* --ws-wash */
        { from: 'surface', percent: 7, base: '#F1F1F1' },   /* --ws-canvas */
        { from: 'surface', percent: 3, base: '#FFFFFF' },   /* --ws-pane */
    ],
    dark: [
        { from: 'accent', percent: 14, base: '#1B1F25' },   /* --ws-wash */
        { from: 'surface', percent: 10, base: '#121316' },  /* --ws-canvas */
        { from: 'surface', percent: 8, base: '#191B1F' },   /* --ws-pane */
    ],
});

/** The neutral a kit falls back to when no lightness of its hue can be read. */
const NEUTRAL_INK = Object.freeze({ light: '#1A1D21', dark: '#ECEEF1' });

/**
 * The three status tones a chip is drawn in — `.ws-chip--ok`, `--amber`, `--danger`:
 * the tone as text on its own wash. Each is a fixed base tinted by the kit's accent,
 * so a pale accent lifts the text toward its wash (backlog B17: the pale kit put
 * danger at 4.44:1 and amber at 4.30:1). Mixes and bases are the sheet's own, as
 * above. The pane and the second pane are grounds too: the same tones are used as
 * text straight on cards.
 */
const STATUS_TONES = Object.freeze({
    light: {
        ok: { ink: [8, '#1C7652'], wash: [6, '#E7F3ED'] },
        warn: { ink: [8, '#8A6116'], wash: [6, '#FAF2DF'] },
        danger: { ink: [10, '#B23B32'], wash: [6, '#FAEDEC'] },
    },
    dark: {
        ok: { ink: [8, '#4FBF8B'], wash: [8, '#15281F'] },
        warn: { ink: [8, '#D9A441'], wash: [8, '#2A2314'] },
        danger: { ink: [10, '#E4796F'], wash: [8, '#2B1A18'] },
    },
});

const PANES = Object.freeze({
    light: [
        { from: 'surface', percent: 3, base: '#FFFFFF' },       /* --ws-pane */
        { from: 'surface_alt', percent: 6, base: '#F7F8FA' },   /* --ws-pane-2 */
    ],
    dark: [
        { from: 'surface', percent: 8, base: '#191B1F' },
        { from: 'surface_alt', percent: 10, base: '#212429' },
    ],
});

const mixed = (kit, from, theme, percent, base) => {
    const top = parseColor(kit[from][theme]);
    const ground = parseColor(base);
    return top && ground ? mixSrgb(top, ground, percent) : ground;
};

/**
 * `--ws-<tone>-ink-<l|d>` for each tone this kit pulls under 4.5:1, and nothing for
 * a tone that already reads — the sheet's own mix stays what is painted there. The
 * hue is held and the lightness walked, as for the accent.
 */
const statusInks = (kit, theme) => {
    const suffix = theme === 'dark' ? 'd' : 'l';
    const panes = PANES[theme].map(({ from, percent, base }) => mixed(kit, from, theme, percent, base));

    return Object.entries(STATUS_TONES[theme]).reduce((held, [tone, { ink, wash }]) => {
        const text = mixed(kit, 'accent', theme, ink[0], ink[1]);
        const grounds = [mixed(kit, 'accent', theme, wash[0], wash[1]), ...panes];
        if (grounds.every((ground) => contrastRatio(text, ground) >= AA_CONTRAST)) return held;

        const readable = readableInk(toHex(text), grounds, theme);
        if (readable) held[`--ws-${tone}-ink-${suffix}`] = readable;
        return held;
    }, {});
};

/** The three grounds, resolved for one theme of one kit. */
const groundsFor = (kit, theme) => INK_GROUNDS[theme]
    .map(({ from, percent, base }) => {
        const top = parseColor(kit[from][theme]);
        const ground = parseColor(base);
        return top && ground ? mixSrgb(top, ground, percent) : ground;
    })
    .filter(Boolean);

/**
 * The twelve custom properties a `[data-brand]` block would have declared, for a
 * kit that has no block — plus the ones this sheet cannot derive for itself.
 *
 * `--ws-accent-ink-*` is the accent as **text**: the kit's own colour where it
 * already reads on every surface it lands on, and the same hue walked darker
 * (light theme) or lighter (dark) until it does. `--ws-on-*` is the kit's contrast
 * colour, kept where it reads on the accent **fill** and replaced with the better
 * of near-black and white where it does not.
 *
 * Only a custom kit comes through here. The six house kits are worn by attribute
 * and their ramp is the sheet's, so nothing below can move them — which is the
 * requirement: an admin-made kit must become readable and a shipped one must not
 * change by a pixel.
 */
export const kitCustomProperties = (tokens) => {
    const normalized = normalizeTokens(tokens);
    if (!normalized) return {};

    const properties = KIT_TOKEN_KEYS.reduce((held, key) => {
        const stem = WS_KIT_STEMS[key];
        held[`--ws-${stem}-l`] = normalized[key].light;
        held[`--ws-${stem}-d`] = normalized[key].dark;
        return held;
    }, {});

    WS_THEMES.forEach((theme) => {
        const suffix = theme === 'dark' ? 'd' : 'l';
        const accent = normalized.accent[theme];

        properties[`--ws-accent-ink-${suffix}`] =
            readableInk(accent, groundsFor(normalized, theme), theme) || NEUTRAL_INK[theme];

        // The pair on the fill, corrected in place: the fill itself is the kit's
        // and is not touched, so a brand stays the colour it was chosen to be.
        properties[`--ws-on-${suffix}`] =
            readableOnAccent(normalized.accent_contrast[theme], accent);

        Object.assign(properties, statusInks(normalized, theme));
    });

    return properties;
};

/** "Halyard" out of "halyard" — the shipped house kits are named by their ids. */
const titleCase = (value) =>
    String(value || '')
        .split(/[\s_-]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

/**
 * What the kit calls itself. The admin portal derives `wordmark_text` from the
 * kit's name at save, so the name is the same fallback here that it is there, and
 * a kit created down the 30-second path is never nameless.
 */
const kitName = (source) => {
    const wordmark = typeof source.wordmark_text === 'string' ? source.wordmark_text.trim() : '';
    return wordmark || titleCase(source.name);
};

/**
 * The kit's own id, or — for a house or certificate kit whose snapshot carries no
 * id — the shipped house its wordmark names. Only the six ids the sheet already
 * has a block for are recognised, and an employer kit is never matched by name, so
 * this can rescue a house that arrived unlabelled without ever costing an employer
 * its own colours. The kit library is not read here or anywhere else.
 */
const snapshotKitId = (source) => {
    const declared = typeof source.brand_kit_id === 'string' ? source.brand_kit_id.trim() : '';
    if (declared || source.kind === 'employer') return declared;

    const named = kitName(source).toLowerCase();
    return isHouseKit(named) ? named : '';
};

/**
 * The auto wordmark: initials of the first two words, so "Acme Robotics" reads
 * "AR" and "Halyard" reads "H". This is the tail of the 30-second kit path —
 * name plus accent, and the mark draws itself.
 */
export const monogramFor = (text) => {
    const words = String(text || '').split(/[\s_-]+/).filter(Boolean);
    if (!words.length) return '';
    return words.slice(0, 2).map((word) => word.charAt(0).toUpperCase()).join('');
};

/**
 * A snapshot logo is a GCS path into a **private** bucket, so it is not an address and
 * this does not try to make one out of it.
 *
 * It used to: `https://storage.googleapis.com/<path>`, which 403s for every viewer, and
 * `HouseMark` swallowed the failure into its glyph — so an employer-branded trial showed
 * no mark and nothing anywhere said why. The server holds the only credential that can
 * sign, and `utils/brandLogo.js` leases a short-lived URL from it. What survives here is
 * the one thing the path still tells a client: whether there is a mark to ask for.
 */
export const logoPath = (path) => String(path || '').trim();

/**
 * `WorkTrial.brand_kit_snapshot` -> everything the workspace needs to wear it.
 *
 * A snapshot that names a shipped house kit is worn by attribute alone. Anything
 * else is worn by its own twelve values. A snapshot with neither — no kit, or a
 * `tokens` map that is not the closed six-key shape — falls back to the neutral
 * house kit, because an unbranded workspace is a fiction break and an Appli-branded
 * one is worse.
 */
export const resolveBrandKit = (snapshot) => {
    const source = snapshot && typeof snapshot === 'object' ? snapshot : {};
    const kitId = snapshotKitId(source);
    const kind = KIT_KINDS.includes(source.kind) ? source.kind : 'house';
    const roleFamily = typeof source.role_family === 'string' ? source.role_family : '';

    const house = isHouseKit(kitId);
    const customProperties = house ? {} : kitCustomProperties(source.tokens);
    const custom = !house && Object.keys(customProperties).length > 0;

    // Neither a shipped house kit nor a usable six-key map: wear the neutral house,
    // and wear it whole. A kit we could not resolve lends the workspace nothing —
    // its name over the neutral house's colours would be the same fiction break as
    // an unbranded one.
    const wearsOwnKit = house || custom;
    const effectiveKitId = wearsOwnKit ? kitId : WS_NEUTRAL_BRAND;
    const authoredName = wearsOwnKit ? kitName(source) : '';

    return {
        brandId: custom ? WS_CUSTOM_BRAND : effectiveKitId,
        kitId: effectiveKitId,
        isHouse: !custom,
        kind,
        roleFamily,
        // A house is named by its id when it authored no wordmark of its own; a
        // custom kit is named by itself and by nothing else, so an unnamed one
        // carries no mark rather than a borrowed one.
        wordmarkText: authoredName || (custom ? '' : titleCase(effectiveKitId)),
        // The path, not a URL — nothing here can resolve one. A kit that fell through to
        // the neutral house wears no mark of its own, so it names no logo either.
        logoPath: wearsOwnKit ? logoPath(source.logo_gcs_path) : '',
        customProperties,
    };
};

/** The neutral house kit, resolved. What a screen outside the provider wears. */
export const NEUTRAL_BRAND = resolveBrandKit({ brand_kit_id: WS_NEUTRAL_BRAND, kind: 'house' });

/** `light` or `dark`. Anything else is not a theme the sheet declares a ramp for. */
export const isTheme = (value) => WS_THEMES.includes(value);

/**
 * The viewer's explicit choice, or `null` while they have not made one. Storage
 * throws in a locked-down browser, and a workspace that will not render because a
 * preference could not be read is worse than one that renders in the system theme.
 */
export const readStoredTheme = () => {
    try {
        const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
        return isTheme(stored) ? stored : null;
    } catch {
        return null;
    }
};

export const writeStoredTheme = (theme) => {
    if (!isTheme(theme)) return;
    try {
        window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
        /* A preference that cannot be persisted still applies for this session. */
    }
};

/** The `(prefers-color-scheme: dark)` query, or `null` where the browser has none. */
export const darkSchemeQuery = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
    return window.matchMedia('(prefers-color-scheme: dark)');
};

export const systemTheme = () => (darkSchemeQuery()?.matches ? 'dark' : 'light');
